import { Link } from "@tanstack/react-router";
import { ContactButton } from "@/components/blocks/ContactButton";
import { Marquee } from "@/components/blocks/decor/Decor";
import { AssembleScene } from "@/components/blocks/scenes/AssembleScene";
import { Cta } from "@/components/blocks/sections/Cta";
import { Faq } from "@/components/blocks/sections/Faq";
import { PageHero } from "@/components/blocks/sections/PageHero";
import { Section } from "@/components/blocks/sections/Section";
import { Button } from "@/components/ui/button";
import { useSectionViewTracking } from "@/lib/analytics";
import { useI18n } from "@/lib/i18n/use-i18n";
import {
	AiDevelopment,
	Collaboration,
	Company,
	FeaturedWork,
	ProductTeaser,
	ServicesOverview,
} from "./HomeSections";

/** Section anchors, also used for `section_view` analytics events. */
const SECTION_IDS = {
	hero: "hero",
	services: "services",
	ai: "ia",
	work: "realisation",
	process: "collaboration",
	company: "entreprise",
	products: "produits",
	faq: "questions",
	cta: "contact",
} as const;

const SECTIONS = Object.values(SECTION_IDS).map((id) => ({ id, label: id }));

export function HomePage() {
	const { locale, dict, localeParams } = useI18n();
	useSectionViewTracking(SECTIONS, { pageType: "home", locale });
	const home = dict.home;
	const hero = home.hero;
	const entryClass =
		"font-medium text-foreground underline decoration-signal/60 underline-offset-4 transition-colors hover:decoration-signal";

	return (
		<>
			<PageHero
				id={SECTION_IDS.hero}
				label={hero.label}
				title={hero.title}
				emphasis={hero.emphasis}
				intro={hero.intro}
				visual={
					<AssembleScene className="mx-auto w-full max-w-[34rem] sm:aspect-[16/12] lg:max-w-none" />
				}
				note={
					<p>
						{hero.entriesLabel}{" "}
						<Link
							to="/{-$locale}/services/applications-metier"
							params={localeParams}
							className={entryClass}
						>
							{hero.entries.businessApps}
						</Link>
						{" · "}
						<Link
							to="/{-$locale}/services/migration-react-native-expo"
							params={localeParams}
							className={entryClass}
						>
							{hero.entries.expoMigration}
						</Link>
					</p>
				}
				footer={
					<div className="border-t py-6">
						<p className="sr-only">{home.marquee.label}</p>
						<Marquee items={Object.values(home.marquee.items)} />
					</div>
				}
			>
				<ContactButton placement="hero">{hero.primaryCta}</ContactButton>
				<Button
					size="lg"
					variant="ghost"
					className="h-auto min-h-11 rounded-lg px-6 font-mono text-[13px] whitespace-normal"
					asChild
				>
					<a href={`#${SECTION_IDS.services}`}>{hero.secondaryCta}</a>
				</Button>
			</PageHero>
			<ServicesOverview id={SECTION_IDS.services} />
			<AiDevelopment id={SECTION_IDS.ai} />
			<FeaturedWork id={SECTION_IDS.work} />
			<Collaboration id={SECTION_IDS.process} />
			<Company id={SECTION_IDS.company} />
			<ProductTeaser id={SECTION_IDS.products} />
			<Section id={SECTION_IDS.faq} tone="stone">
				<Faq title={home.faq.title} items={home.faq.items} />
			</Section>
			<Cta
				id={SECTION_IDS.cta}
				title={home.cta.title}
				description={home.cta.description}
				button={home.cta.button}
				placement="home_cta"
			/>
		</>
	);
}
